import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useToast } from "@/hooks/useToast";
import { LOGIN_RESEND_ERROR, LOGIN_SEND_ERROR } from "@/utils/toastMessages";
import { publicApi } from "@/services/publicApi";
import { useAuth } from "@/hooks/useAuth";
import { isReviewPasswordEmail } from "@/utils/util";
import { Input } from "@/components/Input";
import { Button } from "@/components/Button";
import { PageHead } from "@/components/PageHead";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ArrowRightIcon } from "@/components/icons/icons";
import logger from "@/utils/logger";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { MarketingPanel } from "@/pages/authentication/signup/components/MarketingPanel";
import { SIGNIN_PANEL_CONTENT } from "@/constants/signupPanelContent";
import { AccentButton } from "@/components/buttonVariants";
import { Mail, ArrowRight } from "lucide-react";
import { loginLinkResent } from "@/utils/tracking";
import { EXPERT_SIGNUP_ROLE_PARAM } from "@/utils/roleUtils";

const LoginEmailCheckPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const { showToast } = useToast();
  const { passwordLogin } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // The review account lands on this same screen but enters a password here
  // instead of waiting for an email link. The decision is made from the email
  // that was submitted on /login — no conditional UI on the login screen itself.
  const isReviewMode = isReviewPasswordEmail(email || "");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setPasswordError("Password is required");
      return;
    }
    setPasswordError(undefined);
    setIsSigningIn(true);
    try {
      const { route } = await passwordLogin(email, password);
      navigate(route, { replace: true });
    } catch (error) {
      logger.error(error);
      const status = (error as { response?: { status?: number } })?.response?.status;
      const message =
        status === 401
          ? "Invalid email or password."
          : status === 403
            ? "Password login is not available for this account."
            : LOGIN_SEND_ERROR;
      showToast({ type: "error", message, duration: 6000 });
    } finally {
      setIsSigningIn(false);
    }
  };

  const resendLoginLink = async () => {
    if (!email || isResending || resendCooldown > 0) return;

    setIsResending(true);
    try {
      console.log("Resending login link to:", email);
      await publicApi.auth.resendEmail(email);
      loginLinkResent();
      setResendCooldown(30);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Failed to resend email:", error);
      showToast({ type: "error", ...LOGIN_RESEND_ERROR });
    } finally {
      setIsResending(false);
    }
  };

  // Review account: render the SAME shell as the login screen (LoginPage) so the
  // password step matches it exactly — same layout, card, fonts, Input/Button
  // common components. Only the heading copy and the password field differ.
  if (isReviewMode) {
    return (
      <>
        <PageHead title="Login" description="Sign in to your AchieveOS account" />
        <EnvironmentBadge />

        <div className="flex font-inter min-h-screen bg-signup-form-bg bg-surface-page-bg">
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col items-center px-8 min-h-screen py-16">
              <div className="w-full max-w-[722px] flex flex-col items-center gap-10">
                {/* Logo + Theme Toggle */}
                <div className="w-full flex items-center justify-between">
                  <img
                    src={fsvLogo}
                    alt="AchieveOS"
                    className="logo-light-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <img
                    src={fsvLogoDark}
                    alt="AchieveOS"
                    className="logo-dark-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <ThemeToggle />
                </div>

                {/* Form card — identical styling to LoginPage */}
                <div className="w-full bg-theme-form-panel-bg border border-theme-form-panel-border rounded-2xl p-10">
                  <h1 className="text-[36px] font-medium leading-10 tracking-[-0.35px] text-theme-text-heading">
                    Sign In To Your Account
                  </h1>
                  <p className="text-18 text-theme-text-muted leading-7 tracking-[-0.44px] pt-3">
                    Enter the password for your account to sign in.
                  </p>

                  <form onSubmit={submitPassword} className="pt-8">
                    <Input
                      id="password"
                      type="password"
                      label="Password"
                      required
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={passwordError}
                    />

                    <Button
                      type="submit"
                      variant="primaryGradient"
                      fullWidth
                      isLoading={isSigningIn}
                      disabled={isSigningIn}
                      rightIcon={<ArrowRightIcon />}
                      className="mt-6 h-14 text-16 font-semibold"
                    >
                      Sign In
                    </Button>
                  </form>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-14 font-semibold text-theme-text-heading hover:underline"
                >
                  Try a different email
                </button>
              </div>
            </div>
          </div>

          {/* Right: same marketing panel as LoginPage / the email-check step. */}
          <div className="hidden md:block sticky top-0 self-start h-screen">
            <MarketingPanel
              content={SIGNIN_PANEL_CONTENT(
                () => navigate("/signup"),
                () => navigate(`/signup?role=${EXPERT_SIGNUP_ROLE_PARAM}`),
              )}
            />
          </div>
        </div>
      </>
    );
  }

  // min-h-screen, not h-screen: h-screen + min-h-0 pinned the row to the
  // viewport and clipped whatever didn't fit — with no scrollbar, the bottom
  // of the marketing panel was simply unreachable on short windows.
  return (
    <div className="flex flex-col min-h-screen font-inter bg-signup-form-bg bg-surface-page-bg">
      <div className="flex flex-1">
        {/* Left: light gradient bg, content centered */}
        <div className="flex-1 bg-signup-form-bg bg-surface-page-bg overflow-y-auto">
          <div className="flex flex-col items-center justify-center px-8 min-h-full py-8">
            <div className="w-full max-w-[448px] flex flex-col items-center">
              {/* Logo */}
              <img
                src={fsvLogo}
                alt="AchieveOS"
                className="logo-light-mode h-[50px] w-[218px] max-w-full object-contain self-center mb-8"
              />
              <img
                src={fsvLogoDark}
                alt="AchieveOS"
                className="logo-dark-mode h-[50px] w-[218px] max-w-full object-contain self-center mb-8"
              />

              {/* Card */}
              <div className="bg-surface-card border border-surface-border rounded-2xl w-full shadow-card-subtle overflow-hidden">
                <div className="px-8 pt-8 pb-8 flex flex-col items-center">
                  {/* Email icon */}
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 flex-shrink-0"
                    style={{
                      backgroundImage:
                        "linear-gradient(135deg, rgb(97, 95, 255) 0%, rgb(173, 70, 255) 100%)",
                    }}
                  >
                    <Mail className="text-white" size={28} strokeWidth={1.75} />
                  </div>

                  {/* Heading */}
                  <h1 className="text-2xl font-medium text-center text-theme-text-heading tracking-[0.07px] mb-3">
                    Check your email
                  </h1>

                  {/* Description */}
                  <p className="text-base text-center text-theme-text-muted leading-6 tracking-[-0.3px] mb-6 max-w-[358px]">
                    We have sent a login link to your inbox. The link will expire in 10 minutes.
                  </p>

                  {/* Didn't receive label */}
                  <p className="text-sm text-center text-theme-text-muted tracking-[-0.15px] mb-4">
                    Didn&apos;t receive the email?
                  </p>

                  {/* Send again button */}
                  <AccentButton
                    onClick={resendLoginLink}
                    disabled={isResending || resendCooldown > 0}
                    className="w-full justify-center"
                  >
                    {isResending
                      ? "Sending..."
                      : resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : "Send again"}
                  </AccentButton>

                  {/* Try different email link */}
                  <Link
                    to="/login"
                    className="mt-3 flex items-center gap-1 text-sm font-medium text-purple-600 hover:underline"
                  >
                    Try a different email
                    <ArrowRight size={14} />
                  </Link>

                  {/* Sending to */}
                  {email && (
                    <p className="mt-6 text-xs text-center text-theme-text-muted tracking-[-0.15px]">
                      Sending to:{" "}
                      <span className="font-medium text-theme-text-muted">{email}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Footer links */}
              <div className="flex items-center gap-6 mt-6">
                <button
                  onClick={() => navigate("/legal/terms", { state: { from: location.pathname } })}
                  className="text-sm text-theme-text-muted tracking-[-0.15px] hover:underline"
                >
                  Terms &amp; Conditions
                </button>
                <span className="text-theme-border text-base">•</span>
                <button
                  onClick={() => navigate("/legal/privacy", { state: { from: location.pathname } })}
                  className="text-sm text-theme-text-muted tracking-[-0.15px] hover:underline"
                >
                  Privacy Policy
                </button>
                <span className="text-theme-border text-base">•</span>
                <button
                  onClick={() => navigate("/legal/contact", { state: { from: location.pathname } })}
                  className="text-sm text-theme-text-muted tracking-[-0.15px] hover:underline"
                >
                  Contact Us
                </button>
              </div>
              <p className="text-xs text-theme-text-muted mt-2">
                &copy; 2026 Forward Share Ventures. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* Right: purple marketing panel — the same one /login shows, since
            this page is the next step of that flow. Previously it rendered the
            signup step-1 panel (step progress, stats, testimonial), which
            belongs to a different journey and changed under the user
            mid-sign-in. */}
        <div className="hidden md:block sticky top-0 self-start h-screen">
          <MarketingPanel
            content={SIGNIN_PANEL_CONTENT(
              () => navigate("/signup"),
              () => navigate(`/signup?role=${EXPERT_SIGNUP_ROLE_PARAM}`),
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginEmailCheckPage;
